import React, { useEffect, useState } from "react";
import ReactConfetti from "react-confetti";
import { useWindowSize } from "react-use";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sparkles, Rocket } from "lucide-react";

const RANK_META = {
  Pilot:     { tier: 1, threshold: 100,  color: "from-yellow-200 via-yellow-400 to-yellow-600", accent: "#FFD700", quote: "First stripe earned. The hull is airborne." },
  Commander: { tier: 2, threshold: 300,  color: "from-red-400 via-red-500 to-red-700",         accent: "#E63946", quote: "You command the bridge. The fleet salutes." },
  Navigator: { tier: 3, threshold: 600,  color: "from-yellow-100 via-yellow-300 to-white",     accent: "#FFFFFF", quote: "You chart the course. The legacy is yours." },
};

export const RankUpCinematic = ({ rank, onDone }) => {
  const { width, height } = useWindowSize();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (rank && RANK_META[rank]) {
      setVisible(true);
      const t = setTimeout(() => { setVisible(false); onDone && onDone(); }, 4200);
      return () => clearTimeout(t);
    }
  }, [rank, onDone]);

  if (!rank || !RANK_META[rank]) return null;
  const meta = RANK_META[rank];

  return (
    <AnimatePresence>
      {visible && (
        <>
          <ReactConfetti
            width={width}
            height={height}
            numberOfPieces={500}
            recycle={false}
            gravity={0.18}
            colors={["#FFD700", "#FFC107", "#FFE76A", "#E63946", "#FFFFFF"]}
            style={{ position: "fixed", top: 0, left: 0, zIndex: 80, pointerEvents: "none" }}
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[75] flex items-center justify-center pointer-events-none"
            data-testid="rank-up-cinematic"
          >
            {/* full-screen flash */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.7, 0.25] }}
              transition={{ duration: 1.2, times: [0, 0.25, 1] }}
              className="absolute inset-0"
              style={{ background: `radial-gradient(circle at center, ${meta.accent}44 0%, transparent 60%), black` }}
            />
            {/* scanner beams */}
            <motion.div
              initial={{ rotate: 0, opacity: 0 }}
              animate={{ rotate: 360, opacity: [0, 0.5, 0] }}
              transition={{ duration: 3, ease: "linear" }}
              className="absolute inset-0"
              style={{ background: `conic-gradient(from 0deg, transparent 0deg, ${meta.accent}22 20deg, transparent 40deg, transparent 180deg, ${meta.accent}33 200deg, transparent 220deg, transparent 360deg)` }}
            />

            {/* Central crest */}
            <motion.div
              initial={{ scale: 0.4, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 160, damping: 20, delay: 0.15 }}
              className="relative text-center eigoo-glass rounded-3xl px-10 py-10 max-w-lg mx-4"
              style={{ boxShadow: `0 0 80px ${meta.accent}55, 0 0 30px ${meta.accent}88 inset` }}
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="text-yellow-300" size={16} />
                <div className="text-[10px] uppercase tracking-[0.35em] text-yellow-300 font-display font-bold">Rank Ascended</div>
                <Sparkles className="text-yellow-300" size={16} />
              </div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: [0, 8, -8, 0] }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="h-32 w-32 mx-auto rounded-full flex items-center justify-center relative mb-4"
              >
                <div className="absolute inset-0 rounded-full animate-[spin_12s_linear_infinite]" style={{ background: `conic-gradient(from 0deg, ${meta.accent}, transparent 30%, ${meta.accent} 60%, transparent 90%, ${meta.accent})` }} />
                <div className="absolute inset-[6px] rounded-full bg-gradient-to-br from-black to-zinc-900 border-2 flex items-center justify-center" style={{ borderColor: meta.accent }}>
                  {rank === "Navigator" ? <Trophy style={{ color: meta.accent }} size={48} /> : rank === "Commander" ? <Rocket style={{ color: meta.accent }} size={48} /> : <Trophy style={{ color: meta.accent }} size={48} />}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <div className="text-xs uppercase tracking-[0.25em] text-white/50 font-display">You Are Now</div>
                <div className={`font-display font-black text-5xl md:text-7xl bg-gradient-to-b ${meta.color} bg-clip-text text-transparent leading-none mt-1`}>
                  {rank.toUpperCase()}
                </div>
                <div className="text-white/80 italic mt-4 text-base md:text-lg">"{meta.quote}"</div>
                <div className="text-[10px] uppercase tracking-widest text-yellow-300/70 font-display mt-3">
                  Tier {meta.tier} · {meta.threshold}+ XP
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RankUpCinematic;
